import React, { useState } from 'react';
import {
  RefreshCw,
  CreditCard,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  ShieldAlert,
  HelpCircle,
  FileText,
  ChevronRight,
  Gavel,
  Gift,
  ArrowDownRight,
  Receipt
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

interface NavSection {
  id: string;
  title: string;
  icon: React.ElementType;
}

const sections: NavSection[] = [
  { id: 'overview', title: '1. Overview & Business Model', icon: FileText },
  { id: 'plans', title: '2. Monthly Subscription Plans', icon: Calendar },
  { id: 'credit-rules', title: '3. Credit Usage & Expiration', icon: CreditCard },
  { id: 'cancellation', title: '4. Cancellation Policy', icon: RefreshCw },
  { id: 'refund-eligibility', title: '5. Refund Eligibility', icon: CheckCircle2 },
  { id: 'non-refundable', title: '6. Non-Refundable Situations', icon: XCircle },
  { id: 'no-guarantee', title: '7. No Recruitment Guarantee', icon: AlertTriangle },
  { id: 'payment-issues', title: '8. Duplicate & Failed Payments', icon: Receipt },
  { id: 'processing-time', title: '9. Processing Timelines (14 Days)', icon: Clock },
  { id: 'promos-downgrades', title: '10. Promos & Downgrades', icon: Gift },
  { id: 'disputes', title: '11. Chargebacks & Taxes', icon: ShieldAlert },
  { id: 'contact', title: '12. Billing Support & Submissions', icon: HelpCircle },
];

export const RefundPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Background Cyber Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                NEXGENT FINANCIAL GOVERNANCE
              </span>
              <span className="text-xs text-slate-500">v1.8 • Effective Aug 2026</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              Refund & Cancellation Policy
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <Clock className="w-4 h-4 text-cyan-400" />
            <span>Processing SLA: 14 Working Days</span>
          </div>
        </div>
      </header>

      {/* Main Container Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Policy Index
            </h2>
            <nav className="space-y-1">
              {sections.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Section */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-10">
          
          {/* Section 1 */}
          <section id="overview" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <FileText className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">1. Overview & Commercial Model</h2>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm">
              <strong className="text-cyan-400">Nexgent</strong> (operated by Motherson Infotech) provides SaaS-based executive hiring services, talent matching tools, and credit-based recruiter access. Payments are processed securely via PCI-DSS certified gateways (<strong>Razorpay</strong>).
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              By purchasing or subscribing to any tier on Nexgent, you explicitly acknowledge and consent to the terms, conditions, credit expiration policies, and non-refundable parameters defined within this document.
            </p>
          </section>

          {/* Section 2 */}
          <section id="plans" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Calendar className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">2. Monthly Subscription Plans & Manual Renewal</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              All subscription plans (<strong className="text-white">Ignite</strong>, <strong className="text-white">Accelerate</strong>, <strong className="text-white">Dominate</strong>) operate strictly on a <strong>Monthly Validity Cycle</strong> (30 calendar days from activation).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <div className="p-3.5 bg-slate-950/70 border border-slate-800 rounded-xl space-y-1">
                <div className="text-xs font-mono text-cyan-400 uppercase">Manual Subscription Renewal</div>
                <div className="text-xs text-slate-300">Subscriptions do not automatically charge your payment method upon expiration. Renewal requires explicit manual action and approval by the user every month.</div>
              </div>
              <div className="p-3.5 bg-slate-950/70 border border-slate-800 rounded-xl space-y-1">
                <div className="text-xs font-mono text-cyan-400 uppercase">30-Day Benefit Period</div>
                <div className="text-xs text-slate-300">Features, profile views, and candidate interaction tools remain active for exactly 30 days following successful payment confirmation.</div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section id="credit-rules" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <CreditCard className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">3. Credit Allocation, Usage & Expiration Rules</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Credits allocated as part of a monthly plan or purchased as add-on packages are consumed whenever profile contact details, resumes, or candidate engagement tools are unlocked.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="text-cyan-400 font-semibold font-mono uppercase">Strict Credit Expiration Policy:</div>
              <ul className="list-disc pl-4 text-slate-400 space-y-1">
                <li><strong className="text-white">Monthly Cycle Expiration:</strong> All unused credits expire at the end of the 30-day billing cycle. Unused credits <strong className="text-red-400">do not roll over</strong> into subsequent billing cycles.</li>
                <li><strong className="text-white">Non-Transferable:</strong> Credits possess no real cash value, cannot be exchanged, and are non-transferable between separate accounts.</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section id="cancellation" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <RefreshCw className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">4. Cancellation Policy</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Since Nexgent operates on a <strong>Manual Renewal System</strong>, you are not subjected to recurring auto-debits.
            </p>
            <div className="space-y-2 text-xs">
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800 flex justify-between items-center">
                <span className="text-white font-medium">Stopping Plan Continuation</span>
                <span className="text-cyan-400 font-mono">Simply refrain from manually renewing for the next month</span>
              </div>
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800 flex justify-between items-center">
                <span className="text-white font-medium">Access Duration Upon Non-Renewal</span>
                <span className="text-slate-400 font-mono">Retained until current 30-day term ends</span>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="refund-eligibility" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">5. Refund Eligibility Conditions</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Refunds are strictly exceptional and issued only under specific verified technical or administrative failures:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div className="p-3.5 bg-slate-950/80 rounded-xl border border-slate-800 space-y-1">
                <div className="text-cyan-400 font-semibold">Verified System Failure</div>
                <div className="text-slate-400">Payment debited successfully, but account credits failed to provision after 24 hours due to server bug.</div>
              </div>
              <div className="p-3.5 bg-slate-950/80 rounded-xl border border-slate-800 space-y-1">
                <div className="text-cyan-400 font-semibold">Confirmed Duplicate Charge</div>
                <div className="text-slate-400">Multiple charges generated for a single manual purchase attempt verified by payment logs.</div>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section id="non-refundable" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-950 text-red-400 rounded-lg border border-red-800">
                <XCircle className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">6. Non-Refundable Situations</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Nexgent enforces strict non-refundable parameters to protect digital platform assets:
            </p>
            <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 text-xs space-y-2 text-slate-300">
              <ul className="list-disc pl-4 space-y-1.5 text-slate-400">
                <li><strong className="text-white">No "Cooling-Off" Window:</strong> Digital service delivery starts immediately upon activation. No refunds are provided after payment, even if requested within 7 days.</li>
                <li><strong className="text-white">No Partial Refunds:</strong> Partially used monthly plans, remaining credits, or partially utilized feature packages are completely non-refundable.</li>
                <li><strong className="text-white">Change of Mind:</strong> Re-evaluating recruitment budget, decision to halt hiring, or user error during purchasing.</li>
                <li><strong className="text-white">Policy Violations:</strong> Account suspension or termination resulting from fraudulent activity, candidate scraping, or breach of Terms.</li>
              </ul>
            </div>
          </section>

          {/* Section 7 */}
          <section id="no-guarantee" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-cyan-800/40">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">7. Disclaimer: No Recruitment Guarantee</h2>
            </div>
            <div className="p-4 bg-slate-950/90 border border-slate-800 rounded-xl text-xs space-y-2 text-slate-300">
              <p className="font-semibold text-cyan-400 uppercase font-mono">Core Business Disclaimer:</p>
              <p className="leading-relaxed text-slate-400">
                Nexgent provides technological tools to facilitate candidate discovery. Nexgent <strong>does not guarantee</strong> candidate responses, interview acceptances, employment offers, or recruitment success. Refunds will strictly <span className="text-red-400">not be issued</span> based on dissatisfaction with hiring performance or candidate engagement outcomes.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section id="payment-issues" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Receipt className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">8. Duplicate & Failed Payments</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              If a transaction fails but funds are debited from your bank account, payment providers usually reconcile the amount within 3–5 banking days. If duplicate charges appear on your invoice, notify <strong>billing@nexgent.tech</strong> with transaction receipts for manual credit or reversal.
            </p>
          </section>

          {/* Section 9 */}
          <section id="processing-time" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Clock className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">9. Refund Processing Timelines</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Once an exceptional refund is approved in writing by the Nexgent Billing Department:
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl text-center space-y-1">
              <div className="text-2xl font-mono font-extrabold text-cyan-400">14 WORKING DAYS</div>
              <p className="text-xs text-slate-400">SLA for processing approved refunds through original payment channels (Razorpay / Bank Accounts).</p>
            </div>
          </section>

          {/* Section 10 */}
          <section id="promos-downgrades" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Gift className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">10. Promotional Credits & Downgrades</h2>
            </div>
            <div className="space-y-3 text-xs text-slate-300">
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <strong className="text-white block mb-1">Promotional & Free Credits</strong>
                Discounts, promotional credits, complimentary trial access, or special event credits carry zero cash value, are non-refundable, and expire strictly as per promotional terms.
              </div>
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <strong className="text-white block mb-1">Plan Downgrades</strong>
                When choosing a lower-tier plan for the subsequent month (e.g., from <em>Dominate</em> to <em>Ignite</em>), unused benefits from the higher tier will not carry over or entitle the user to cash adjustments.
              </div>
            </div>
          </section>

          {/* Section 11 */}
          <section id="disputes" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldAlert className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">11. Chargebacks, Disputes & Taxes</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Users agree to contact Nexgent internal billing support prior to raising payment disputes or chargebacks with issuing banks. Fraudulent or abusive chargeback claims will lead to immediate, permanent account suspension. Applicable statutory taxes (e.g., GST) are non-refundable where mandated by law.
            </p>
          </section>

          {/* Section 12 */}
          <section id="contact" className="scroll-mt-28 space-y-6 bg-slate-900/80 p-6 md:p-8 rounded-2xl border border-cyan-800/50 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <HelpCircle className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">12. Billing Support & Refund Submission</h2>
                <p className="text-xs text-slate-400">Submitting requests to the billing desk</p>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              To submit a billing inquiry or report a technical payment error, email our team with your <strong>Transaction ID</strong>, <strong>Invoice Number</strong>, and <strong>Registered Email ID</strong>.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs pt-2">
              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-slate-500 font-mono">BILLING DESK</div>
                <div className="text-sm font-bold text-cyan-400">billing@nexgent.tech</div>
                <div className="text-slate-400">Support: support@nexgent.tech</div>
              </div>

              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-slate-500 font-mono">CORPORATE ADDRESS</div>
                <div className="text-sm font-bold text-white">Motherson Infotech</div>
                <div className="text-slate-400">Pune, Maharashtra, India</div>
              </div>
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-cyan-500 font-mono">
              {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent. All rights reserved. */}
              Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First

            </div>
          </section>

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default RefundPolicy;